import { useState, useEffect, useRef, useCallback } from 'react';
import { ImageReel } from './ImageReel';
import { Header } from './Header';
import { Photo } from '../types';
import { SeamlessVideo } from './SeamlessVideo';
import { Volume2, VolumeX } from 'lucide-react@0.487.0';

export interface HeroConfig {
  mode: 'video' | 'images';
  videoUrl?: string | null;
  videoFileName?: string | null;
  audioUrl?: string | null;
  audioFileName?: string | null;
}

interface HeroProps {
  onExploreClick: () => void;
  photos: Photo[];
  cartItemCount: number;
  onCartClick: () => void;
  onGalleryClick: () => void;
  onHomeClick: () => void;
  onAboutClick?: () => void;
  onPrintsClick?: () => void;
  heroConfig?: HeroConfig;
  onReady?: () => void;
}

export function Hero({ onExploreClick, photos, cartItemCount, onCartClick, onGalleryClick, onHomeClick, onAboutClick, onPrintsClick, heroConfig, onReady }: HeroProps) {
  const wantsVideo = heroConfig?.mode === 'video' && !!heroConfig?.videoUrl;
  const [videoFailed, setVideoFailed] = useState(false);
  const videoReadyRef = useRef(false);
  const fallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Audio state
  const audioARef = useRef<HTMLAudioElement | null>(null);
  const audioBRef = useRef<HTMLAudioElement | null>(null);
  const activeAudioRef = useRef<'A' | 'B'>('A');
  const crossfadeTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [isMuted, setIsMuted] = useState(true);
  const [audioReady, setAudioReady] = useState(false);
  const hasAudio = !!heroConfig?.audioUrl;

  // Crossfade duration in seconds
  const AUDIO_CROSSFADE = 3;
  const AUDIO_VOLUME = 0.35;

  // When config changes, reset failure state
  useEffect(() => {
    setVideoFailed(false);
    // Only reset videoReady if the video URL actually changed
    // (avoids race condition where heroConfig re-renders reset an already-ready video)
  }, [heroConfig?.mode, heroConfig?.videoUrl]);

  // Audio setup — dual-element crossfade loop
  useEffect(() => {
    if (!heroConfig?.audioUrl) {
      setAudioReady(false);
      [audioARef, audioBRef].forEach(ref => {
        if (ref.current) { ref.current.pause(); ref.current = null; }
      });
      return;
    }

    const createAudio = () => {
      const a = new Audio(heroConfig.audioUrl!);
      a.preload = 'auto';
      a.volume = 0;
      return a;
    };

    const audioA = createAudio();
    const audioB = createAudio();
    audioARef.current = audioA;
    audioBRef.current = audioB;
    activeAudioRef.current = 'A';

    const onCanPlay = () => setAudioReady(true);
    audioA.addEventListener('canplaythrough', onCanPlay);

    return () => {
      audioA.removeEventListener('canplaythrough', onCanPlay);
      if (crossfadeTimerRef.current) clearInterval(crossfadeTimerRef.current);
      [audioA, audioB].forEach(a => { a.pause(); a.src = ''; });
      audioARef.current = null;
      audioBRef.current = null;
      setAudioReady(false);
    };
  }, [heroConfig?.audioUrl]);

  // Crossfade loop controller
  useEffect(() => {
    const aA = audioARef.current;
    const aB = audioBRef.current;
    if (!aA || !aB || !audioReady) return;

    if (crossfadeTimerRef.current) {
      clearInterval(crossfadeTimerRef.current);
      crossfadeTimerRef.current = null;
    }

    if (isMuted) {
      aA.pause();
      aB.pause();
      aA.volume = 0;
      aB.volume = 0;
      return;
    }

    // Start the active audio
    const active = activeAudioRef.current === 'A' ? aA : aB;
    active.volume = AUDIO_VOLUME;
    active.play().catch(() => setIsMuted(true));

    // Poll for crossfade timing
    crossfadeTimerRef.current = setInterval(() => {
      const curr = activeAudioRef.current === 'A' ? aA : aB;
      const next = activeAudioRef.current === 'A' ? aB : aA;

      if (!curr.duration || !isFinite(curr.duration)) return;
      const timeLeft = curr.duration - curr.currentTime;

      // Start crossfade
      if (timeLeft <= AUDIO_CROSSFADE && next.paused) {
        next.currentTime = 0;
        next.volume = 0;
        next.play().catch(() => {});
      }

      // Ramp volumes during crossfade
      if (timeLeft <= AUDIO_CROSSFADE) {
        const progress = 1 - (timeLeft / AUDIO_CROSSFADE); // 0→1
        // Use equal-power crossfade curve for smooth transition
        const fadeOut = Math.cos(progress * Math.PI / 2);
        const fadeIn = Math.sin(progress * Math.PI / 2);
        curr.volume = Math.max(0, Math.min(1, fadeOut * AUDIO_VOLUME));
        next.volume = Math.max(0, Math.min(1, fadeIn * AUDIO_VOLUME));
      }

      // Swap when current ends
      if (curr.ended || timeLeft < 0.05) {
        curr.pause();
        curr.volume = 0;
        next.volume = AUDIO_VOLUME;
        activeAudioRef.current = activeAudioRef.current === 'A' ? 'B' : 'A';
      }
    }, 50);

    return () => {
      if (crossfadeTimerRef.current) clearInterval(crossfadeTimerRef.current);
    };
  }, [isMuted, audioReady]);

  const toggleMute = useCallback(() => {
    setIsMuted(prev => !prev);
  }, []);

  // If in video mode, start a fallback timer.
  // If SeamlessVideo hasn't fired onReady within timeout → fall back to ImageReel.
  const videoUrlForTimerRef = useRef<string | null>(null);

  useEffect(() => {
    if (!wantsVideo) return;

    const currentUrl = heroConfig?.videoUrl ?? null;

    // If URL hasn't changed and we already got ready, skip
    if (currentUrl === videoUrlForTimerRef.current && videoReadyRef.current) {
      return;
    }

    // New URL — reset ready state
    if (currentUrl !== videoUrlForTimerRef.current) {
      videoReadyRef.current = false;
      videoUrlForTimerRef.current = currentUrl;
    }

    fallbackTimerRef.current = setTimeout(() => {
      if (!videoReadyRef.current) {
        console.warn('[Hero] Video did not become ready in 30 s — falling back to image reel');
        setVideoFailed(true);
      }
    }, 30000);

    return () => {
      if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current);
    };
  }, [wantsVideo, heroConfig?.videoUrl]);

  const onReadyStableRef = useRef(onReady);
  onReadyStableRef.current = onReady;

  const handleVideoReady = useCallback(() => {
    videoReadyRef.current = true;
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
    onReadyStableRef.current?.();
  }, []);

  const handleVideoError = useCallback(() => {
    console.warn('[Hero] SeamlessVideo reported error — falling back to image reel');
    setVideoFailed(true);
  }, []);

  // Decide what to actually render
  const showVideo = wantsVideo && !videoFailed;

  return (
    <div className="relative min-h-[140svh] h-[140svh] m-0 p-0 bg-black">
      <div className="absolute top-0 left-0 right-0 z-20">
        <Header 
          cartItemCount={cartItemCount}
          onCartClick={onCartClick}
          onGalleryClick={onGalleryClick}
          onHomeClick={onHomeClick}
          onAboutClick={onAboutClick}
          onPrintsClick={onPrintsClick}
          onExploreClick={onExploreClick}
          transparent={true}
        />
      </div>
      
      {/* Background: video or image reel */}
      <div className="absolute inset-0 z-0">
        {showVideo ? (
          <SeamlessVideo
            src={heroConfig!.videoUrl!}
            fadeDuration={1.5}
            onReady={handleVideoReady}
            onError={handleVideoError}
          />
        ) : (
          <ImageReel photos={photos} onReady={onReady} />
        )}
        {/* Dark overlay for both modes */}
        {showVideo && (
          <div className="absolute inset-0 bg-black/25 pointer-events-none" />
        )}
        {/* Bottom fade so hero blends seamlessly into gallery below */}
        <div className="absolute inset-x-0 bottom-0 h-[40%] pointer-events-none" style={{ background: 'linear-gradient(to top, rgb(0,0,0) 0%, rgba(0,0,0,0.95) 10%, rgba(0,0,0,0.8) 25%, rgba(0,0,0,0.5) 45%, rgba(0,0,0,0.25) 65%, rgba(0,0,0,0.08) 82%, transparent 100%)' }} />
      </div>

      {/* Hero content */}
      <div className="absolute inset-0 flex items-center justify-center z-10">
        <div className="text-center px-6 md:px-8 max-w-5xl">
          {/* Main headline with Freight Display Pro */}
          <h1 
            className="mb-8 md:mb-12 text-yellow-400"
            style={{
              fontFamily: 'var(--font-freight)',
              fontSize: 'clamp(2.5rem, 6vw, 3.5rem)',
              fontWeight: 400,
              lineHeight: 1.3,
              letterSpacing: '0.05em'
            }}
          >
          </h1>
          
          {/* Subtitle - MUCH LARGER ON MOBILE */}
          <p className="max-w-2xl mx-auto text-white/85 mb-10 md:mb-14 tracking-wide leading-relaxed text-lg md:text-base font-light">
            Limited edition photographic prints on museum-quality fine art paper. 
            Each piece comes with a certificate of authenticity and edition number.
          </p>
          
          {/* CTA Button removed */}
        </div>
      </div>

      {/* Audio toggle button — sticky within viewport, bottom-right */}
      {hasAudio && audioReady && (
        <div className="sticky bottom-0 h-0 z-30 pointer-events-none">
          <button
            onClick={toggleMute}
            className="absolute bottom-6 right-6 pointer-events-auto p-3 bg-black/40 backdrop-blur-sm border border-white/15 text-white/70 hover:text-white hover:bg-black/60 transition-all duration-300 rounded-full"
            aria-label={isMuted ? 'Kveikja á hljóði' : 'Slökkva á hljóði'}
            title={isMuted ? 'Spila tónlist' : 'Þagga tónlist'}
          >
            {isMuted ? (
              <VolumeX className="w-5 h-5" />
            ) : (
              <Volume2 className="w-5 h-5" />
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export default Hero;